import React, { PropsWithChildren, useContext } from 'react'
import Label from './Label'
import GeoContext from 'contexts/geoContext'
import PartnershipIcon from 'components/Common/Vendors/Partnership/PartnershipIcon'

type Variant = 'use-only' | 'earn-and-use' | 'earn-points' | 'earn-only' | 'vendor';
type Config = Omit<React.ComponentProps<typeof Label>, 'children'> & {
  children: (partnership: App.Partnership, points?: number) => React.ReactNode;
}

const variantConfig: Record<Variant, Config> = {
  'use-only': {
    kind: 'secondary',
    variant: 'ghost',
    startIcon: <PartnershipIcon />,
    children: (partnership: App.Partnership) => `Use ${partnership.rewardName}`,
  },
  'earn-and-use': {
    kind: 'secondary',
    variant: 'ghost',
    startIcon: <PartnershipIcon />,
    children: (partnership: App.Partnership) => `Earn & Use ${partnership.rewardName}`,
  },
  'earn-only': {
    kind: 'secondary',
    variant: 'ghost',
    startIcon: <PartnershipIcon />,
    children: (partnership: App.Partnership) => `Earn ${partnership.rewardName}`,
  },
  'earn-points': {
    kind: 'plain',
    variant: 'dark',
    startIcon: <PartnershipIcon />,
    children: (partnership, points) =>
      `Earn ${points ? Math.floor(points).toLocaleString() : 0} ${partnership.rewardName}`
    ,
  },
  vendor: {
    kind: 'secondary',
    variant: 'dark',
    children: () => null,
  },
}

interface Props {
  variant?: 'default' | 'vendor';
  canEarn?: boolean;
  canUse?: boolean;
  points?: number;
  className?: string;
}

function PartnershipLabel(props: PropsWithChildren<Props>) {
  const { variant = 'default', canEarn = true, canUse = true, className, children, points } = props

  const { partnership } = useContext(GeoContext)

  if (!partnership) {
    // can't show a label for a non-existant partnership
    return null
  }

  const hasEarn = canEarn && partnership.hasEarn
  const hasUse = canUse && partnership.hasBurn

  let configType: Variant | undefined
  if (variant === 'vendor') {
    configType = 'vendor'
  } else if (points) {
    configType = 'earn-points'
  } else {
    if (hasEarn && hasUse) {
      configType = 'earn-and-use'
    } else if (hasEarn) {
      configType = 'earn-only'
    } else if (hasUse) {
      configType = 'use-only'
    }
  }

  if (!configType) {
    // guess we couldn't do anything
    return null
  }

  const config = variantConfig[configType]

  return <Label
    {...config}
    className={className}
    data-testid="partnership-label"
  >
    {variant === 'vendor' ? children : config.children(partnership, points)}
  </Label>
}

export default PartnershipLabel
